import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  LinearProgress,
  Divider,
  Button,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  Pending as PendingIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { useToast } from "../../components/Toast";
import { formatCurrency, formatDate } from "../../utils/utils";
import api from "../../services/api";
import PaymentDue from "./PaymentDue";

// Payment Status Types
export interface PaymentOrderData {
  id: string;
  orderId: string;
  companyId: string;
  orderAmount: string;
  orderCurrency: string;
  orderStatus: string;
  paymentSessionId: string;
  customerId: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  orderNote: string;
  orderType: string;
  noOfUnits: number;
  unitPrice: number;
  discountedUnitPrice: number;
  onboardingAmount: number;
  orderTags: {
    dueDate: string;
    companyId: string;
    createdBy: string;
    orderType: string;
  };
  returnUrl: string;
  notifyUrl: string | null;
  orderExpiryTime: string;
  referenceId: string;
  referenceType: string;
  description: string;
  discountAmount: string;
  taxAmount: string;
  totalAmount: string;
  paymentDueDate: string;
  isRecurring: boolean;
  recurringFrequency: string;
  nextBillingDate: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  company: {
    id: string;
    name: string;
    email: string;
  };
  payments: any[]; // Array of payment transactions
  refunds: any[]; // Array of refunds
}

interface PaymentStatusResponse {
  success: boolean;
  data: PaymentOrderData;
  message?: string;
}

const PaymentStatus: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [paymentData, setPaymentData] = useState<PaymentOrderData | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [isPaymentSuccessful, setIsPaymentSuccessful] = useState(false);
  const { showToast } = useToast();

  // Check if payment is successful
  const checkPaymentSuccess = (data: PaymentOrderData): boolean => {
    const hasSuccessfulOrder = data.orderStatus?.toLowerCase() === "paid";
    const hasSuccessfulPayment = data.payments?.some(
      (payment) => payment.paymentStatus?.toLowerCase() === "success"
    );
    return hasSuccessfulOrder && hasSuccessfulPayment;
  };

  // Start countdown for successful payments
  useEffect(() => {
    if (paymentData && isPaymentSuccessful && countdown === null) {
      setCountdown(5);
    }
  }, [paymentData, isPaymentSuccessful, countdown]);

  // Handle countdown timer
  useEffect(() => {
    if (countdown !== null && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0) {
      // Navigate to dashboard when countdown reaches 0
      navigate("/dashboard");
    }
  }, [countdown, navigate]);

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "paid":
      case "success":
      case "successful":
        return "success";
      case "failed":
      case "failure":
        return "error";
      case "pending":
      case "initiated":
        return "warning";
      case "refunded":
        return "info";
      case "cancelled":
      case "canceled":
        return "default";
      case "active":
        return "warning";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case "paid":
      case "success":
      case "successful":
        return <CheckCircleIcon color="success" />;
      case "failed":
      case "failure":
        return <CancelIcon color="error" />;
      case "pending":
      case "initiated":
      case "active":
        return <ScheduleIcon color="warning" />;
      case "refunded":
        return <RefreshIcon color="info" />;
      default:
        return <PendingIcon />;
    }
  };

  const fetchPaymentStatus = async () => {
    if (!orderId) {
      setError("Order ID is required");
      return;
    }

    setLoading(true);
    setError(null);
    setPaymentData(null);
    setLoadingProgress(0);
    setCountdown(null);
    setIsPaymentSuccessful(false);

    try {
      // Add 5 second delay with progress updates
      const totalDelay = 5000;
      const intervalDelay = 100;
      const progressIncrement = (intervalDelay / totalDelay) * 100;

      await new Promise((resolve) => {
        let currentProgress = 0;
        const progressInterval = setInterval(() => {
          currentProgress += progressIncrement;
          setLoadingProgress(Math.min(currentProgress, 100));

          if (currentProgress >= 100) {
            clearInterval(progressInterval);
            resolve(undefined);
          }
        }, intervalDelay);
      });

      const endpoint = `/payments/orders/${encodeURIComponent(orderId)}`;
      const response = await api.get<PaymentStatusResponse>(endpoint);

      if (response.data.success) {
        setPaymentData(response.data.data);
        const isSuccessful = checkPaymentSuccess(response.data.data);
        setIsPaymentSuccessful(isSuccessful);

        if (isSuccessful) {
          showToast(
            "Payment successful! Redirecting to dashboard...",
            "success"
          );
        } else {
          showToast("Payment status retrieved successfully", "info");
        }
      } else {
        setError(response.data.message || "Payment not found");
        showToast(response.data.message || "Payment not found", "error");
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || "Failed to fetch payment status";
      setError(errorMessage);
      showToast(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentStatus();
  }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = () => {
    fetchPaymentStatus();
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleCancelNavigation = () => {
    setCountdown(null);
    setIsPaymentSuccessful(false);
  };

  const renderSuccessMessage = () => {
    if (!isPaymentSuccessful || countdown === null) return null;

    return (
      <Alert
        severity="success"
        variant="outlined"
        sx={{
          mb: 3,
          border: 2,
          borderColor: "success.main",
          backgroundColor: "transparent",
          color: "success.main",
        }}
        icon={<CheckCircleIcon sx={{ color: "success.main" }} />}
        action={
          <Button
            variant="outlined"
            size="small"
            onClick={handleCancelNavigation}
            sx={{
              color: "success.main",
              borderColor: "success.main",
              fontWeight: "bold",
              "&:hover": {
                backgroundColor: "success.light",
                borderColor: "success.main",
              },
            }}
          >
            Stay Here
          </Button>
        }
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", color: "success.main", mb: 0.5 }}
            >
              Payment Successful!
            </Typography>
            <Typography variant="body2" sx={{ color: "success.dark" }}>
              Redirecting to dashboard in {countdown} second
              {countdown !== 1 ? "s" : ""}
            </Typography>
          </Box>
          <CircularProgress
            variant="determinate"
            value={(5 - countdown) * 20}
            size={40}
            sx={{
              color: "success.main",
              "& .MuiCircularProgress-circle": {
                strokeLinecap: "round",
              },
            }}
          />
        </Box>
      </Alert>
    );
  };

  const renderNoPaymentsFound = () => {
    if (!paymentData) return null;

    // Transform the data to match PaymentDue's expected interface
    const transformedPaymentData = {
      ...paymentData,
      notifyUrl: paymentData.notifyUrl || "", // Convert null to empty string
      noOfUnits: paymentData?.noOfUnits,
      unitPrice: paymentData?.unitPrice,
      discountedUnitPrice: paymentData?.discountedUnitPrice,
    };

    return (
      <Box sx={{ mt: 3 }}>
        {/* Show PaymentDue component for payment processing */}
        <Alert severity="error" sx={{ mb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              No Payments Found
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Payment is pending. Please try again.
          </Typography>
        </Alert>
        <PaymentDue
          paymentDue={{
            isSuccess: true,
            data: [transformedPaymentData], // PaymentDue expects an array
          }}
        />
      </Box>
    );
  };

  const renderPaymentsList = () => {
    if (
      !paymentData ||
      !paymentData.payments ||
      paymentData.payments.length === 0
    ) {
      return null;
    }

    return (
      <Card sx={{ mt: 3, border: 0, boxShadow: "none" }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Payment History ({paymentData.payments.length})
          </Typography>
          {paymentData.payments.map((payment: any, index: number) => (
            <Box
              key={index}
              sx={{
                mb: 2,
                p: 2,
                bgcolor: "#ffffff",
                borderRadius: 1,
                border:
                  payment.paymentStatus?.toLowerCase() === "success" ? 2 : 1,
                borderColor:
                  payment.paymentStatus?.toLowerCase() === "success"
                    ? "success.main"
                    : "divider",
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Payment ID
                  </Typography>
                  <Typography variant="body2">
                    {payment.paymentId || payment.id || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {getStatusIcon(payment.paymentStatus)}
                    <Chip
                      label={payment.paymentStatus || "Unknown"}
                      color={getStatusColor(payment.paymentStatus)}
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Amount
                  </Typography>
                  <Typography variant="body2">
                    {payment.paymentAmount
                      ? formatCurrency(parseFloat(payment.paymentAmount))
                      : payment.amount
                      ? formatCurrency(parseFloat(payment.amount))
                      : "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Date
                  </Typography>
                  <Typography variant="body2">
                    {payment.paymentTime
                      ? formatDate(payment.paymentTime)
                      : payment.createdAt
                      ? formatDate(payment.createdAt)
                      : "N/A"}
                  </Typography>
                </Grid>
                {payment.paymentMethod && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Payment Method
                    </Typography>
                    <Typography variant="body2">
                      {payment.paymentMethod}
                    </Typography>
                  </Grid>
                )}
                {payment.bankReference && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Bank Reference
                    </Typography>
                    <Typography variant="body2">
                      {payment.bankReference}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          ))}
        </CardContent>
      </Card>
    );
  };

  const theme = useTheme();

  if (!orderId) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          height: "100vh",
          width: "100vw",
          bgcolor: "#ffffff",
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
          position: "fixed",
          top: 0,
          left: 0,
          zIndex: 9999,
        }}
      >
        <Container
          sx={{
            py: 3,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            overflow: "auto",
          }}
        >
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="h6">Order ID Required</Typography>
            <Typography variant="body2">
              No Order ID provided in the URL. Please provide a valid Order ID
              to check payment status.
            </Typography>
          </Alert>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={handleGoBack}
            sx={{ alignSelf: "flex-start" }}
          >
            Go Back
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        height: "100vh",
        width: "100vw",
        bgcolor: "#ffffff",
        display: "flex",
        flexDirection: "column",
        overflow: "auto",
        position: "fixed",
        top: 0,
        left: 0,
        zIndex: 9999,
      }}
    >
      <Container
        sx={{
          py: 3,
          flex: 1,
          maxWidth: "100%",
          width: "100%",
          overflow: "auto",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleGoBack}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Box>
            <Typography variant="h4" gutterBottom>
              Payment Status
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Order ID: {orderId}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
            sx={{ ml: "auto" }}
          >
            Refresh
          </Button>
        </Box>

        {/* Loading State */}
        {loading && (
          <Card sx={{ mt: 3, border: 0, boxShadow: "none" }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  py: 4,
                }}
              >
                <CircularProgress
                  variant="determinate"
                  value={loadingProgress}
                  size={80}
                  thickness={4}
                />
                <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                  Fetching payment status...
                </Typography>

                <Box sx={{ width: "100%", maxWidth: 400, mb: 2 }}>
                  <LinearProgress
                    variant="determinate"
                    value={loadingProgress}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>

                <Typography
                  variant="body1"
                  color="primary"
                  sx={{ fontWeight: "bold" }}
                >
                  {Math.round(loadingProgress)}% complete
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1, textAlign: "center" }}
                >
                  Please wait while we retrieve your payment information from
                  the server
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 2 }}
                >
                  This may take a few moments...
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="body1">{error}</Typography>
          </Alert>
        )}

        {/* Success Message with Countdown */}
        {renderSuccessMessage()}

        {/* Payment Details */}
        {!loading && paymentData && (
          <>
            {/* Check if payments array is empty */}
            {!paymentData.payments || paymentData.payments.length === 0
              ? renderNoPaymentsFound()
              : renderPaymentsList()}

            {/* Show refunds if any */}
            {paymentData.refunds && paymentData.refunds.length > 0 && (
              <Card sx={{ mt: 3, border: 0, boxShadow: "none" }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Refund History ({paymentData.refunds.length})
                  </Typography>
                  {paymentData.refunds.map((refund: any, index: number) => (
                    <Box
                      key={index}
                      sx={{
                        mb: 2,
                        p: 2,
                        bgcolor: "#ffffff",
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="body2">
                        Refund #{index + 1}:{" "}
                        {refund.amount
                          ? formatCurrency(parseFloat(refund.amount))
                          : "N/A"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {refund.createdAt
                          ? formatDate(refund.createdAt)
                          : "N/A"}
                      </Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};

export default PaymentStatus;
